import React, { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import { createLocale } from 'locales';

import type { CrumbType } from 'components-new/breadcrumbs/crumb';

import Breadcrumbs from 'components-new/breadcrumbs/breadcrumbs';

import Typography from 'components-new/typography/typography';

import Banner from 'components-new/banner/banner';
import useClosePeriodJob from 'state/remote/remote-organization/use-close-period-job';
import useHidePeriodJob from 'state/remote/remote-organization/use-hide-period-job';

import Button from 'components-new/button/button';

import type { OrganisationPeriodJob } from 'state/__generated__/tadaahAPI';

import Modal from 'components-new/modal/modal';

import useGetView from './use-get-view';

const localize = createLocale('organisationPeriodJobsLocales');

interface HeaderPropoTypes {
  periodJobs: OrganisationPeriodJob[];
}

const Header: React.FC<HeaderPropoTypes> = (props) => {
  const { periodJobs } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const { showAssignmentDetails, showJobConfirm, showJobDetails, showList } =
    useGetView();

  const page = useMemo(():
    | 'details'
    | 'confirm'
    | 'list'
    | 'assignmentDetails' => {
    if (showJobDetails) return 'details';
    if (showJobConfirm) return 'confirm';
    if (showAssignmentDetails) return 'assignmentDetails';
    return 'list';
  }, [showJobDetails, showJobConfirm, showAssignmentDetails]);

  const title = useMemo((): string => {
    if (showJobDetails && periodJobs.length && periodJobs[0].organisation)
      return periodJobs[0].organisation;
    if (showJobConfirm) return localize('JobConfirm');
    if (showAssignmentDetails && periodJobs.length && periodJobs[0].location)
      return periodJobs[0].location;
    return localize('overview');
  }, [showJobDetails, periodJobs, showJobConfirm, showAssignmentDetails]);

  const { closePeriodJob } = useClosePeriodJob(() =>
    navigate('/organisation/period_jobs'),
  );
  const { hidePeriodJob } = useHidePeriodJob(() =>
    navigate('/organisation/period_jobs'),
  );

  const crumbs = [
    {
      name: localize('assignments'),
      onClick: () => {
        navigate('/organisation/planner');
      },
    },
    {
      name: localize('PeriodJobs'),
    },
  ] as CrumbType[];

  if (searchParams.get('job_id') && Array.from(searchParams).length < 2) {
    crumbs[1].onClick = () => {
      navigate('/organisation/period_jobs');
    };
    crumbs.push({
      name: localize('jobDetails'),
    });
  }

  if (searchParams.get('confirm') === 'true') {
    const periodJobsCrumb = crumbs.find(
      (value) => !!value.name && value.name === localize('PeriodJobs'),
    );
    if (periodJobsCrumb)
      periodJobsCrumb.onClick = () => {
        navigate(
          `/organisation/period_jobs/?job_id=${searchParams.get('job_id')}`,
        );
      };
    crumbs.push({
      name: localize('jobDetails'),
    });
    const details = crumbs.find(
      (value) => !!value.name && value.name === localize('jobDetails'),
    );
    if (details)
      details.onClick = () => {
        navigate(
          `/organisation/period_jobs/?job_id=${searchParams.get('job_id')}`,
        );
      };
    crumbs.push({
      name: localize('JobConfirm'),
    });
  }

  if (searchParams.get('assignment_details') === 'true') {
    crumbs[1].onClick = () => {
      navigate(`/organisation/period_jobs/`);
    };
    crumbs.push({
      name: localize('dashboard'),
    });
  }
  const periodJob: OrganisationPeriodJob | undefined = useMemo(() => {
    return periodJobs.find(
      (job) => job.id?.toString() === searchParams.get('job_id'),
    );
  }, [periodJobs, searchParams]);

  const mutateJobStatus = useCallback(() => {
    if (periodJob?.status === 'O')
      closePeriodJob({ jobId: searchParams.get('job_id') || '' });
    else if (periodJob?.status === 'C')
      hidePeriodJob({ jobId: searchParams.get('job_id') || '' });
  }, [closePeriodJob, searchParams, periodJob, hidePeriodJob]);

  return (
    <div className="mt-10 w-full px-6 md:px-10 md:w-[90%] lg:w-[80%] flex flex-col sm:flex-row items-start justify-start sm:justify-between sm:items-start">
      <div className=" flex flex-col items-start justify-start">
        <Breadcrumbs crumbs={crumbs} />
        <Typography
          variant="heading-1"
          as="h3"
          className={classNames(
            'mt-5',
            page === 'details' && 'flex justify-start items-center',
          )}
        >
          {title}
          {page === 'details' && (
            <Banner
              title={classNames(
                periodJob?.status === 'O' && localize('open'),
                periodJob?.status === 'C' && localize('closed'),
              )}
              variant="destructive"
              className="ml-2 bg-[#F7DCDA] text-destructive-500 rounded-md"
            />
          )}
        </Typography>
      </div>
      {showList && (
        <Button
          label={localize('newAssignement')}
          variant="primary"
          className="mt-3 md:mt-auto w-full sm:w-auto"
          onClick={() =>
            window.location.assign('/organisation/period_jobs/create/')
          }
        />
      )}
      {showJobDetails && (
        <Button
          label={classNames(
            periodJob?.status === 'O' && localize('close'),
            periodJob?.status === 'C' && localize('hide'),
          )}
          variant="danger"
          className="mt-3 w-32 md:mt-auto"
          labelClassName="mx-4"
          onClick={() => {
            searchParams.set('confirm_hide', 'true');
            setSearchParams(searchParams);
          }}
        />
      )}
      {showAssignmentDetails && (
        <Button
          label="Invite freelancer"
          variant="primary"
          className="mt-3 md:mt-auto hidden"
          onClick={() => {}}
        />
      )}
      <Modal
        isOpen={!!searchParams.get('confirm_hide')}
        onClose={() => {
          searchParams.delete('confirm_hide');
          setSearchParams(searchParams);
        }}
        title={localize('confirmAction')}
        footer={{
          helpText: localize('clickToProceed', {
            action: classNames(
              periodJob?.status === 'O' && localize('close'),
              periodJob?.status === 'C' && localize('hide'),
            ),
          }),
          primaryButton: (
            <Button
              label={localize('confirm')}
              variant="danger"
              onClick={mutateJobStatus}
            />
          ),
          secondaryButton: (
            <Button
              label={localize('back')}
              variant="secondary"
              onClick={() => {
                searchParams.delete('confirm_hide');
                setSearchParams(searchParams);
              }}
            />
          ),
        }}
      >
        <Typography as="p" variant="body-large" className=" text-gray-700">
          {classNames(
            periodJob?.status === 'O' && localize('confirmCloseJob'),
            periodJob?.status === 'C' && localize('confirmHideJob'),
          )}
        </Typography>
      </Modal>
    </div>
  );
};

export default Header;
